import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useHistory } from 'react-router-dom';
import { SubmitProps } from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faBadgeCheck } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { UserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  getFinishedFeedbackButton,
  getFinishedReportText,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackAwaiting';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

interface TextProps {
  heading: string;
  subHeadingOne?: string;
  subHeadingTwo?: string;
  button?: {
    text: string;
    link: string;
  };
}

const npo: TextProps = {
  heading: 'Your responses are on the way to our team!',
  subHeadingOne:
    'Our amazing Social Impact team is on it! They’ll review your responses as quickly as they can. Thanks for being awesome.',
  subHeadingTwo:
    'You can see any additional tasks for you in the Do To section of your account.',
  button: {
    text: 'View my To Do list',
    link: '/npo/todo/by-due-date',
  },
};

const text: { [x in UserType]: TextProps } = {
  npo,
  'cn-npo': { ...npo, button: undefined },
  donor: {
    heading: 'Your responses are on the way to our team!',
    subHeadingOne:
      'Our amazing Social Impact team is on it! They’ll review your responses as quickly as they can. Thanks for being awesome.',
    subHeadingTwo:
      'You can see any additional tasks for you in the Feedback section of your account.',
    button: {
      text: 'View feedback dashboard',
      link: getFinishedFeedbackButton()?.link || '/donor/feedback',
    },
  },
  'ti staff': {
    heading: 'Your responses are on the way to the NPO!',
    // subHeadingOne:
    //   'Our amazing Social Impact team is on it! They’ll review your responses as quickly as they can. Thanks for being awesome.',
    subHeadingTwo:
      'You can see any additional tasks for you in the Feedback section of your account.',
    button: {
      text: 'View feedback dashboard',
      link: getFinishedFeedbackButton()?.link || '/account/feedback',
    },
  },
};

interface FeedbackDoneScreenProps extends Omit<SubmitProps, 'onDone'> {
  nextFeedbackStatus: FeedbackStatus | undefined;
  className?: string;
}

const StyledDiv = styled.div`
  height: 100%;
  padding-top: 76px;
  background-color: ${COLORS.whiteBase};
  padding-left: ${SPACERS.large};
  padding-right: ${SPACERS.large};
  text-align: center;
  .icon {
    width: 40px;
    height: 40px;
    margin-bottom: ${SPACERS.medium};
  }
  .content-heading {
    margin-bottom: ${SPACERS.small};
  }
  .first-content,
  .second-content {
    margin-left: auto;
    margin-right: auto;
    max-width: 478px;
  }
  .first-content {
    margin-bottom: ${SPACERS.small};
  }
  .second-content {
    margin-bottom: ${SPACERS.extraLarge};
  }
  .done-button {
    margin-bottom: ${SPACERS.small};
  }
`;

interface FeedbackDoneScreenCommonProps
  extends FeedbackDoneScreenProps,
    Omit<TextProps, 'button'> {
  icon: IconProp | IconDefinition;
  iconColor: keyof typeof COLORS;
  button?: {
    text: string;
    link: string;
  };
}

const FeedbackDoneScreenCommon: React.FC<FeedbackDoneScreenCommonProps> = ({
  className,
  heading,
  subHeadingOne,
  subHeadingTwo,
  button,
  icon,
  iconColor,
}) => {
  const { push } = useHistory();
  return (
    <StyledDiv className={className}>
      <FontAwesomeIcon icon={icon} color={COLORS[iconColor]} className="icon" />
      <TrueImpactText className="content-heading" variant="heading">
        {heading}
      </TrueImpactText>
      {subHeadingOne && (
        <TrueImpactText className="first-content">
          {subHeadingOne}
        </TrueImpactText>
      )}
      {subHeadingTwo && (
        <TrueImpactText className="second-content">
          {subHeadingTwo}
        </TrueImpactText>
      )}
      {button && (
        <TrueImpactButtonWithLoading
          onClick={() => push(button.link)}
          className="done-button"
        >
          {button.text}
        </TrueImpactButtonWithLoading>
      )}
    </StyledDiv>
  );
};

const FeedbackDoneScreen: React.FC<FeedbackDoneScreenProps> = props => {
  const { nextFeedbackStatus } = props;
  const { userStaticType } = useUserActions();

  const isPublished =
    nextFeedbackStatus === 'Published' || nextFeedbackStatus === 'Finalized'
      ? getFinishedReportText(nextFeedbackStatus, userStaticType)
      : undefined;

  const userBasedText = text[userStaticType];
  const publishedText = isPublished || userBasedText;

  return (
    <FeedbackDoneScreenCommon
      {...props}
      icon={isPublished ? faBadgeCheck : faPaperPlane}
      heading={publishedText.heading}
      subHeadingOne={publishedText.subHeadingOne}
      subHeadingTwo={publishedText.subHeadingTwo}
      button={userBasedText.button}
      iconColor={isPublished ? 'success' : 'ctaPrimary'}
    />
  );
};

export default FeedbackDoneScreen;
